import { useState, useEffect, memo, useCallback } from "react";
import * as XLSX from 'xlsx';
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import TablaGenerica from "./tablas";
import { obtenerCartera } from "../../functions/consultas";
import { leerArchivoCartera } from "../../functions/importar_cartera";
import { asistenteCargarCartera, dialogo, escapar, mostrarError, mostrarEspera, nota, notas } from "./dialogos_cartera";
import { actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import BotonSOS from "./sos/boton_sos";
import "bootstrap-icons/font/bootstrap-icons.css";

const COLUMNAS = [
  { label: "Fecha",                 key: "fecha",                tipo: "texto"  },
  { label: "Dirección",             key: "tercero",              tipo: "texto"  },
  { label: "Administración",        key: "administracion",       tipo: "moneda" },
  { label: "Retroactivo",           key: "retroactivo",          tipo: "moneda" },
  { label: "Interés por mora",      key: "interes_mora",         tipo: "moneda" },
  { label: "Seguro Áreas Privadas", key: "seguro_areas_privadas",tipo: "moneda" },
  { label: "Multas y Sanciones",    key: "multas_sanciones",     tipo: "moneda" },
  { label: "Total Cartera",         key: "total_cartera",        tipo: "moneda" },
  { label: "Meses de mora",         key: "meses_prom",           tipo: "numero" },
];

const COLUMNAS_EDITABLES = COLUMNAS.filter(c => c.key !== "fecha");

const formatearMoneda = (valor) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valor || 0);

const DrawerEdicion = ({ item, onGuardar, onCerrar, guardando }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (item) setForm({ ...item });
  }, [item]);

  if (!item) return null;

  const cambiar = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <>
      <div
        onClick={onCerrar}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1100 }}
      />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 100vw)",
        background: "#fff", zIndex: 1101, display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <span style={{ fontWeight: "700", fontSize: "15px", color: "#1f2937" }}>Editar registro</span>
          <button onClick={onCerrar} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#6b7280", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "16px", margin: "0 0 16px 0" }}>
            {item.tercero} — {item.fecha}
          </p>
          {COLUMNAS_EDITABLES.map(({ key, label, tipo }) => (
            <div key={key} style={{ marginBottom: "14px", width: "100%" }}>
              <label style={{
                display: "block", fontSize: "12px", fontWeight: "600",
                color: "#374151", marginBottom: "5px", width: "100%"
              }}>
                {label}
              </label>
              <input
                type={tipo === "moneda" || tipo === "numero" ? "number" : "text"}
                value={form[key] ?? ""}
                onChange={e => cambiar(key, e.target.value)}
                style={{
                  display: "block",
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#1f2937",
                  background: "#fff",
                  outline: "none",
                  margin: "0",
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                  appearance: "none",
                  WebkitAppearance: "none",
                  boxShadow: "none",
                  maxWidth: "100%",
                  minWidth: "0",
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ padding: "16px 20px", borderTop: "1px solid #e5e7eb", display: "flex", gap: "10px" }}>
          <button
            onClick={() => onGuardar(form)}
            disabled={guardando}
            style={{ flex: 1, padding: "10px", background: "#c0392b", color: "#fff", border: "none", borderRadius: "7px", fontWeight: "600", fontSize: "13px", cursor: guardando ? "not-allowed" : "pointer", opacity: guardando ? 0.7 : 1 }}
          >
            {guardando ? "Guardando..." : "Guardar cambios"}
          </button>
          <button
            onClick={onCerrar}
            style={{ padding: "10px 16px", background: "#f9fafb", color: "#374151", border: "1px solid #d1d5db", borderRadius: "7px", fontSize: "13px", cursor: "pointer" }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </>
  );
};


const estiloExportar = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", border: "none", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, cursor: "pointer" };

const ListadoCartera = memo(() => {
  const [datos, setDatos]           = useState([]);
  const [datosRaw, setDatosRaw]     = useState({});
  const [cargando, setCargando]     = useState(true);
  const [itemEditar, setItemEditar] = useState(null);
  const [guardando, setGuardando]   = useState(false);

  const capturarDatos = useCallback(async () => {
    try {
      const data = await obtenerCartera();
      const raw = Array.isArray(data) ? Object.fromEntries(data.map((d, i) => [d.id || i, d])) : (data || {});
      setDatosRaw(raw);
      const formateados = Object.entries(raw).map(([id, item]) => ({
        ...item,
        _firebaseId: id,
        administracion:        formatearMoneda(item.administracion),
        retroactivo:           formatearMoneda(item.retroactivo),
        interes_mora:          formatearMoneda(item.interes_mora),
        seguro_areas_privadas: formatearMoneda(item.seguro_areas_privadas),
        multas_sanciones:      formatearMoneda(item.multas_sanciones),
        total_cartera:         formatearMoneda(item.total_cartera),
      }));
      setDatos(formateados);
    } catch (error) {
      console.error(error);
    } finally {
      setCargando(false);
    }
  }, []);

  const enviarCarteraAFirebase = useCallback(async (datos) => {
    try {
      const nit = localStorage.getItem("CodigoCliente");
      if (!nit) throw new Error("No se encontró el código de cliente");
      await actualizarRegistro(`/coordi_ph/${nit}/cartera`, datos);
      return { exito: true, mensaje: "Datos guardados correctamente" };
    } catch (error) {
      return { exito: false, mensaje: `Error: ${error.message}` };
    }
  }, []);

  const manejarImportacion = useCallback(async () => {
    const nit = localStorage.getItem("CodigoCliente") || "";
    const guardo = await asistenteCargarCartera({
      leer: (archivo, formato, codigosL) => leerArchivoCartera(archivo, { formato, codigosL, nit }),
      guardar: enviarCarteraAFirebase,
    });
    if (guardo) await capturarDatos();
  }, [enviarCarteraAFirebase, capturarDatos]);

  const exportarExcel = useCallback(() => {
    const filas = Object.values(datosRaw).map(item =>
      COLUMNAS.map(c => (c.tipo === "texto" ? item[c.key] ?? "" : Number(item[c.key]) || 0)));
    const hoja = XLSX.utils.aoa_to_sheet([COLUMNAS.map(c => c.label), ...filas]);
    hoja["!cols"] = COLUMNAS.map(c => ({ wch: Math.max(c.label.length + 2, c.key === "tercero" ? 28 : 14) }));
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Cartera");
    XLSX.writeFile(libro, "listado_cartera.xlsx");
  }, [datosRaw]);

  const abrirEditar = useCallback((fila) => {
    const id = fila._firebaseId;
    const raw = datosRaw[id] || fila;
    setItemEditar({ ...raw, _firebaseId: id });
  }, [datosRaw]);

  const guardarEdicion = useCallback(async (form) => {
    setGuardando(true);
    try {
      const nit = localStorage.getItem("CodigoCliente");
      const id  = form._firebaseId;
      const { _firebaseId, ...datos } = form;
      await actualizarRegistro(`/coordi_ph/${nit}/cartera/${id}`, datos);
      setItemEditar(null);
      await capturarDatos();
    } catch {
      await mostrarError("No se pudo guardar el registro", "Los cambios no se guardaron. Revise su conexión a internet e intente de nuevo.");
    } finally {
      setGuardando(false);
    }
  }, [capturarDatos]);

  const eliminarItem = useCallback(async (fila) => {
    const { isConfirmed } = await dialogo({
      icono: "bi-trash", tono: "rojo",
      titulo: "¿Eliminar la cartera de este inmueble?",
      texto: `Se borrará la cartera de <strong>${escapar(fila.tercero)}</strong>. El residente dejará de verla en la opción Pagos hasta que se cargue un archivo nuevo.`,
      contenido: notas(nota("peligro", "bi-exclamation-triangle", "Esta acción no se puede deshacer.")),
      confirmar: "Eliminar", iconoConfirmar: "bi-trash",
      cancelar: "Cancelar", focusCancel: true,
    });
    if (!isConfirmed) return;
    mostrarEspera("Eliminando", "Estamos borrando la cartera de este inmueble.");
    try {
      const nit = localStorage.getItem("CodigoCliente");
      await eliminarRegistro(`/coordi_ph/${nit}/cartera/${fila._firebaseId}`);
      await dialogo({ icono: "bi-check-circle", tono: "verde", titulo: "Cartera eliminada",
        texto: `Se borró la cartera de <strong>${escapar(fila.tercero)}</strong>.`, confirmar: "Listo" });
      await capturarDatos();
    } catch {
      await mostrarError("No se pudo eliminar", "La cartera de este inmueble sigue igual. Revise su conexión a internet e intente de nuevo.");
    }
  }, [capturarDatos]);

  const eliminarTodaLaCartera = useCallback(async () => {
    const cantidad = Object.keys(datosRaw).length;
    if (cantidad === 0) {
      await dialogo({ icono: "bi-inbox", tono: "azul", titulo: "No hay cartera cargada",
        texto: "Este conjunto no tiene cartera para eliminar. Puede cargarla con el botón de subir archivo." });
      return;
    }
    const { isConfirmed } = await dialogo({
      icono: "bi-trash", tono: "rojo",
      titulo: "¿Eliminar toda la cartera?",
      texto: `Se borrará la cartera de los <strong>${cantidad}</strong> inmuebles del conjunto. Los residentes dejarán de ver su estado de cuenta en la opción Pagos hasta que se cargue un archivo nuevo.`,
      contenido: notas(
        nota("info", "bi-download", "Si quiere guardar una copia, cancele y use primero el botón de descargar Excel."),
        nota("peligro", "bi-exclamation-triangle", "Esta acción no se puede deshacer.")),
      confirmar: "Eliminar todo", iconoConfirmar: "bi-trash",
      cancelar: "Cancelar", focusCancel: true,
    });
    if (!isConfirmed) return;
    mostrarEspera("Eliminando la cartera", `Estamos borrando la cartera de ${cantidad} inmuebles. No cierre esta página.`);
    try {
      const nit = localStorage.getItem("CodigoCliente");
      if (!nit) throw new Error("No se encontró el código del conjunto. Cierre sesión y vuelva a entrar.");
      await eliminarRegistro(`/coordi_ph/${nit}/cartera`);
      await dialogo({ icono: "bi-check-circle", tono: "verde", titulo: "Cartera eliminada",
        texto: `Se borró la cartera de ${cantidad} inmuebles. Ya puede cargar un archivo nuevo con el botón de subir archivo.`, confirmar: "Listo" });
    } catch (error) {
      await mostrarError("No se pudo eliminar la cartera", `La cartera sigue igual. Detalle: ${escapar(error.message)}`);
    }
    await capturarDatos();
  }, [datosRaw, capturarDatos]);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  if (cargando) return <AnimacionCarga />;

  return (
    <>
      <BarraSuperior />

      <DrawerEdicion
        item={itemEditar}
        onGuardar={guardarEdicion}
        onCerrar={() => setItemEditar(null)}
        guardando={guardando}
      />

      <TablaGenerica
        datos={datos}
        titulos={COLUMNAS.map(c => c.label.toUpperCase())}
        campos={COLUMNAS.map(c => c.key)}
        elementosPorPagina={3000}
        direccionOrdenInicial="desc"
        botones={[
          { texto: "Editar", icono: "bi-pencil", accion: abrirEditar  },
          { texto: "Eliminar", icono: "bi-trash", accion: eliminarItem },
        ]}
        contenidoExtra={
          <>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <button onClick={manejarImportacion} title="Cargar cartera desde Excel o CSV"><i className="bi bi-upload" /></button>
            <button onClick={exportarExcel} style={estiloExportar} title="Descargar Excel"><i className="bi bi-download" /></button>
            <button onClick={eliminarTodaLaCartera} title="Eliminar toda la cartera"><i className="bi bi-trash" /></button>
          </>
        }
      />

      <BotonSOS />
    </>
  );
});

ListadoCartera.displayName = "ListadoCartera";
export default ListadoCartera;
